import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { siteInfo, stats } from "@/mocks/site";

interface Props {
  lead: Article;
  briefs: Article[];
}

export default function HeroSection({ lead, briefs }: Props) {
  return (
    <section className="px-4 md:px-8 pt-6 md:pt-10" aria-labelledby="hero-title">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <article className="lg:col-span-2 relative overflow-hidden rounded-lg bg-foreground-950">
          <Link to={`/article/${lead.id}`} className="block relative aspect-[16/10] md:aspect-[16/9]">
            <img
              src={lead.cover}
              alt={`${lead.title} 题图`}
              title={`${lead.title} - 黑料每日`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
            <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-8">
              <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-accent-950 text-[11px] md:text-xs font-medium mb-3">
                <i className="ri-price-tag-3-line" aria-hidden="true" />
                今日一瓜 · {lead.category}
              </span>
              <h1
                id="hero-title"
                className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-snug mb-3 max-w-2xl"
              >
                {lead.title}
              </h1>
              <p className="text-sm md:text-base text-background-100/90 leading-relaxed max-w-2xl line-clamp-2">
                {lead.excerpt}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3 md:gap-4 text-xs text-background-100/80">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
                  {lead.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
                  {lead.readTime}
                </span>
                <span className="hidden sm:flex items-center gap-1">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
                  {lead.date}
                </span>
              </div>
            </div>
          </Link>
        </article>

        <aside aria-labelledby="today-brief-title" className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 id="today-brief-title" className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-sun-line text-primary-500" aria-hidden="true" />
              今日早报
            </h2>
            <Link to="/morning" className="text-xs text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap">
              全部 <i className="ri-arrow-right-s-line align-middle" aria-hidden="true" />
            </Link>
          </div>
          <ol className="flex flex-col divide-y divide-background-200">
            {briefs.map((item, index) => (
              <li key={item.id} className="py-3 first:pt-0 last:pb-0">
                <Link to={`/article/${item.id}`} className="group flex items-start gap-3">
                  <span className="shrink-0 mt-0.5 w-6 h-6 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-heading text-sm font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </span>
                    <span className="block text-[11px] text-foreground-400 mt-1">{item.category}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ol>
          <p className="mt-4 pt-4 border-t border-background-200 text-[11px] text-foreground-500 flex items-center gap-1.5">
            <i className="ri-refresh-line" aria-hidden="true" />
            {siteInfo.updateTime}
          </p>
        </aside>
      </div>

      <div className="mt-6 md:mt-8 grid grid-cols-2 md:grid-cols-4 gap-px rounded-lg overflow-hidden bg-background-200 border border-background-200">
        {stats.map((item) => (
          <div key={item.label} className="bg-background-50 px-4 py-5 text-center">
            <p className="font-heading text-xl md:text-2xl font-bold text-primary-600">{item.value}</p>
            <p className="text-xs text-foreground-500 mt-1">{item.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}